import { httpClient, toClientApiError } from "./httpClient";

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toClientApiError(error);
  }
}

export type MessageCenterMessage = {
  id: string;
  type: string;
  title: string;
  content: string;
  vote_allowed_roles: string[] | null;
  suggestion_status: string | null;
  admin_note: string | null;
  action_label: string | null;
  action_url: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  author_first: string;
  author_last: string;
  author_role: string;
  vote_count: number;
  avg_rating: number;
  my_rating: number | null;
  isRead: boolean;
};

export type BugReportDto = {
  id: string;
  title: string;
  content: string;
  status: "open" | "in_progress" | "resolved";
  admin_note: string | null;
  created_at: string;
  updated_at: string;
  user_id?: string;
  user_first?: string;
  user_last?: string;
  user_role?: string;
};

export type MessageCenterListResponse = {
  items: MessageCenterMessage[];
  unreadCount: number;
  personalUpdatesCount: number;
  pendingSuggestionsCount: number;
  openReportsCount: number;
  lastSeenMessageCreatedAt: string | null;
  capabilities: {
    isAdmin: boolean;
    canSuggest: boolean;
    roleKeys: string[];
  };
};

export const messagesApi = {
  list(): Promise<MessageCenterListResponse> {
    return wrap(async () => {
      const res = await httpClient.get<MessageCenterListResponse>("/messages");
      return res.data;
    });
  },

  markSeen(body: {
    lastSeenMessageCreatedAt?: string | null;
    markPersonalSeen?: boolean;
  }) {
    return wrap(async () => {
      await httpClient.put("/messages/seen", body);
    });
  },

  createSystemMessage(body: {
    type: "system" | "whats_new";
    title: string;
    content: string;
    audience?: string;
  }) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: unknown }>("/messages/system", body);
      return res.data.item;
    });
  },

  createSuggestion(body: {
    title: string;
    content: string;
    voteAllowedRoles?: string[];
  }) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: unknown }>("/messages/suggestion", body);
      return res.data.item;
    });
  },

  listPendingSuggestions() {
    return wrap(async () => {
      const res = await httpClient.get<{ items: MessageCenterMessage[] }>(
        "/messages/suggestions/pending",
      );
      return res.data.items;
    });
  },

  approveSuggestion(id: string, adminNote?: string, voteAllowedRoles?: string[]) {
    return wrap(async () => {
      await httpClient.patch(`/messages/suggestions/${id}/approve`, {
        adminNote,
        voteAllowedRoles,
      });
    });
  },

  rejectSuggestion(id: string, adminNote?: string) {
    return wrap(async () => {
      await httpClient.patch(`/messages/suggestions/${id}/reject`, { adminNote });
    });
  },

  handleSuggestion(id: string, adminNote?: string) {
    return wrap(async () => {
      await httpClient.patch(`/messages/suggestions/${id}/handle`, { adminNote });
    });
  },

  vote(id: string, rating: number) {
    return wrap(async () => {
      const res = await httpClient.post<{ voteCount: number; avgRating: number }>(
        `/messages/${id}/vote`,
        { rating },
      );
      return res.data;
    });
  },

  deleteMessage(id: string) {
    return wrap(async () => {
      await httpClient.delete(`/messages/${id}`);
    });
  },

  createBugReport(body: { title: string; content: string }) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: BugReportDto }>("/messages/report", body);
      return res.data.item;
    });
  },

  listBugReports() {
    return wrap(async () => {
      const res = await httpClient.get<{ items: BugReportDto[] }>("/messages/reports");
      return res.data.items;
    });
  },

  listMyBugReports() {
    return wrap(async () => {
      const res = await httpClient.get<{ items: BugReportDto[] }>("/messages/reports/mine");
      return res.data.items;
    });
  },

  updateBugReport(
    id: string,
    body: { status?: BugReportDto["status"]; adminNote?: string | null },
  ) {
    return wrap(async () => {
      await httpClient.patch(`/messages/reports/${id}`, body);
    });
  },

  deleteBugReport(id: string) {
    return wrap(async () => {
      await httpClient.delete(`/messages/reports/${id}`);
    });
  },
};
